import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import { getAssociatedEntityDocumentRelationshipInputs } from '@/modules/documents/documentAssociations.utils';
import { isClientProfileDocumentType } from '@/modules/documents/documents.utils';
import { DocumentDetails_DocumentFragment } from '@/modules/documents/graphql/DocumentDetails.generated';
import { useDeleteDocumentMutation } from '@/modules/files/DocumentRepresentation/graphql/DocumentRepresentation.generated';
import { useUpdateGenericDocumentMutation } from '@/modules/files/hooks/graphql/UseUpdateDocument.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import * as diagnostics from '@/utils/diagnostics';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { DocumentDetailsDocument } from '../../graphql/DocumentDetails.generated';
import {
  DocumentDetailsFormShape,
  ValidatedDocumentDetailsFormShape,
} from '../DocumentDetailsForm.types';

export function useSaveDocumentDetailsForm(
  initialDocument?: DocumentDetails_DocumentFragment
) {
  const client = useApolloClient();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const { householdId } = useHouseholdDetailsContext();

  const [updateDocumentMutation, { loading: updateLoading }] =
    useUpdateGenericDocumentMutation({
      refetchQueries: [DocumentDetailsDocument],
      onCompleted: () => {
        showFeedback(FeedbackMessages.formSaveSuccess, {
          variant: 'success',
        });
      },
      onError: (error) => {
        diagnostics.warn('failed to update document', { error });
        const userFacingMessages = getUserFacingErrorMessages(error);
        showFeedback(
          userFacingMessages.length > 0
            ? userFacingMessages.join('. ')
            : FeedbackMessages.formSaveError
        );
      },
    });
  const [deleteDocumentMutation, { loading: deleteLoading }] =
    useDeleteDocumentMutation({
      onCompleted: async () => {
        await client.refetchQueries({
          updateCache(cache) {
            cache.evict({ fieldName: 'documents' });
            cache.gc();
          },
        });
      },
      onError: (error) => {
        reportError(`failed to delete document`, error);
        showFeedback(FeedbackMessages.deleteError);
      },
    });

  const updateDocument = useCallback(
    (formData: ValidatedDocumentDetailsFormShape) => {
      if (!householdId) {
        throw new Error('householdId is required'); // type-safe check. this should never happen.
      }

      const {
        _documentId: documentId,
        fileName,
        documentType,
        isDefaultForEntity,
        associatedClientProfileId,
        associatedEntityIds,
      } = formData;

      // Partition the associated entity vs. TE IDs
      const entityIds = associatedEntityIds.filter(
        (id) => getPulidKind(id) === PulidKind.Entity
      );
      const testamentaryEntityIds = associatedEntityIds.filter(
        (id) => getPulidKind(id) === PulidKind.TestamentaryEntity
      );

      // Generate both create and delete relationship inputs using the utility function
      const { createInputs, deleteInputs } =
        getAssociatedEntityDocumentRelationshipInputs({
          householdId,
          documentId,
          entityIds,
          testamentaryEntityIds,
          initialDocument,
        });

      const clientProfileID =
        isClientProfileDocumentType(documentType) && !!associatedClientProfileId
          ? associatedClientProfileId
          : undefined;
      const clearClientProfile =
        !isClientProfileDocumentType(documentType) ||
        !associatedClientProfileId;

      return updateDocumentMutation({
        variables: {
          documentId,
          documentInput: {
            addAdditionalEntityIDs: entityIds,
            clearAdditionalEntities: true,
            addAdditionalTestamentaryEntityIDs: testamentaryEntityIds,
            clearAdditionalTestamentaryEntities: true,
            type: documentType,
            name: fileName,
            clearDefaultDocumentOfEntity: !isDefaultForEntity,
            clientProfileID,
            clearClientProfile,
          },
          createEntityDocumentRelationshipInputs: createInputs,
          deleteEntityDocumentRelationshipInputs: deleteInputs,
        },
      });
    },
    [householdId, updateDocumentMutation, initialDocument]
  );

  const deleteDocument = useCallback(
    (documentId: string) => {
      return deleteDocumentMutation({
        variables: {
          documentId,
        },
      });
    },
    [deleteDocumentMutation]
  );

  const validateForm = useCallback(
    (form: DocumentDetailsFormShape): ValidatedDocumentDetailsFormShape => {
      if (form.documentKind === '') {
        throw new Error('Document type is required');
      } else {
        return {
          ...form,
          documentType: form.documentKind,
        };
      }
    },
    []
  );

  return {
    updateDocument,
    deleteDocument,
    loading: updateLoading || deleteLoading,
    validateForm,
  };
}
